import { useSnapshot } from 'valtio';

import Select, { type SelectItem } from '@cartwhl/ui/atoms/Select';
import Tooltip from '@cartwhl/ui/atoms/Tooltip';
import TextNew from '@cartwhl/ui/base/TextNew';
import CrossIcon from '@cartwhl/ui/icons/CrossIcon';
import GridIcon from '@cartwhl/ui/icons/GridIcon';
import ListIcon from '@cartwhl/ui/icons/ListIcon';
import SearchIcon from '@cartwhl/ui/icons/SearchIcon';
import SortIcon from '@cartwhl/ui/icons/SortIcon';

import { userStore } from 'stores/global';

import { useAssetLibraryEmbedConfig } from '../assetLibraryEmbedContext';
import {
  ASSET_LIBRARY_LAST_MODIFIED_FILTER_OPTIONS,
  getAssetLibraryTypeFilterOptions,
} from '../assetLibraryFilters';
import { useStore } from '../assetLibraryStore';
import { isTeamPlan } from '../utils';
import AssetLibraryPeopleFilter from './AssetLibraryPeopleFilter';

const FILTER_SELECT_INACTIVE_CLASS_NAME =
  'bg-secondary hover:bg-gray-30 dark:hover:bg-gray-60 w-full min-w-0 border-0';
const FILTER_SELECT_ACTIVE_CLASS_NAME =
  'bg-blue/10 hover:bg-blue/15 dark:bg-blue/20 dark:hover:bg-blue/30 w-full min-w-0 border-0';
const FILTER_CLEAR_BUTTON_CLASS_NAME =
  'bg-blue/10 hover:bg-blue/15 dark:bg-blue/20 dark:hover:bg-blue/30 flex h-10 w-10 flex-none items-center justify-center rounded-lg transition-colors';
const SEARCH_WIDTH_CLASS = 'w-[20rem] max-w-full';
const FILTER_DROPDOWN_WIDTH_CLASS = 'w-[11.5rem] max-w-full';
const VIEW_TOOL_BUTTON_CLASS =
  'border-primary text-primary hover:bg-secondary flex h-10 w-10 flex-none items-center justify-center rounded-lg border transition-colors';

export interface AssetLibraryFiltersProps {
  embedded?: boolean;
}

export default function AssetLibraryFilters({
  embedded = false,
}: AssetLibraryFiltersProps) {
  const store = useStore();
  const embedConfig = useAssetLibraryEmbedConfig();
  const organizationMembers = useSnapshot(userStore.state).organizationMembers;
  const organizationInfo = useSnapshot(userStore.state).organizationInfo;
  const { filters, sortAscending, viewMode } = store.state;
  const typeFilterOptions = getAssetLibraryTypeFilterOptions(embedConfig);
  const showPeopleFilter = isTeamPlan(organizationInfo?.planType);
  const hasTypeFilter = filters.typeFilter !== 'all';
  const hasLastModifiedFilter = filters.lastModifiedFilter !== 'all';
  const hasPeopleFilter = Boolean(filters.createdByUser);

  return (
    <div className="flex flex-col gap-4">
      <label
        className={`bg-secondary flex h-10 items-center gap-2 rounded-lg py-2.5 pr-2 pl-4 ${SEARCH_WIDTH_CLASS}`}
      >
        <span className="sr-only">Search library assets</span>
        <TextNew
          as="input"
          token="14/medium"
          type="search"
          value={filters.searchQuery}
          placeholder="Search library"
          className="placeholder:text-tertiary text-primary min-w-0 flex-1 bg-transparent outline-none"
          onChange={(event) => store.setSearchQuery(event.currentTarget.value)}
        />
        <SearchIcon className="text-primary size-5 flex-none" />
      </label>

      <div className="flex flex-wrap items-center gap-4">
        <FilterSelectField
          embedded={embedded}
          placeholder="Type"
          widthClass={FILTER_DROPDOWN_WIDTH_CLASS}
          showClear={hasTypeFilter}
          value={hasTypeFilter ? filters.typeFilter : undefined}
          items={typeFilterOptions}
          onValueChange={(value) =>
            store.setTypeFilter(
              value as (typeof typeFilterOptions)[number]['value'],
            )
          }
          onClear={() => store.setTypeFilter('all')}
        />
        <FilterSelectField
          embedded={embedded}
          placeholder="Last modified"
          widthClass={FILTER_DROPDOWN_WIDTH_CLASS}
          showClear={hasLastModifiedFilter}
          value={hasLastModifiedFilter ? filters.lastModifiedFilter : undefined}
          items={ASSET_LIBRARY_LAST_MODIFIED_FILTER_OPTIONS}
          onValueChange={(value) =>
            store.setLastModifiedFilter(
              value as (typeof ASSET_LIBRARY_LAST_MODIFIED_FILTER_OPTIONS)[number]['value'],
            )
          }
          onClear={() => store.setLastModifiedFilter('all')}
        />
        {showPeopleFilter ? (
          <AssetLibraryPeopleFilter
            embedded={embedded}
            widthClass={FILTER_DROPDOWN_WIDTH_CLASS}
            showClear={hasPeopleFilter}
            value={filters.createdByUser || undefined}
            organizationMembers={organizationMembers}
            onValueChange={(userID) => store.setCreatedByUserFilter(userID)}
            onClear={() => store.setCreatedByUserFilter('')}
          />
        ) : null}
        <div className="ml-auto flex items-center gap-2">
          <Tooltip
            text={viewMode === 'list' ? 'Grid view' : 'List view'}
            side="top"
            inModal={embedded}
          >
            <button
              type="button"
              aria-label={
                viewMode === 'list'
                  ? 'Switch to grid view'
                  : 'Switch to list view'
              }
              aria-pressed={viewMode === 'list'}
              className={`${VIEW_TOOL_BUTTON_CLASS} ${
                viewMode === 'list' ? 'bg-secondary' : ''
              }`}
              onClick={() => store.toggleViewMode()}
            >
              {viewMode === 'grid' ? (
                <ListIcon className="size-5" />
              ) : (
                <GridIcon className="size-5" />
              )}
            </button>
          </Tooltip>
          <Tooltip
            text={
              sortAscending
                ? 'Sort newest to oldest'
                : 'Sort oldest to newest'
            }
            side="top"
            inModal={embedded}
          >
            <button
              type="button"
              aria-label={
                sortAscending
                  ? 'Sort newest to oldest'
                  : 'Sort oldest to newest'
              }
              aria-pressed={sortAscending}
              className={`${VIEW_TOOL_BUTTON_CLASS} ${
                sortAscending ? 'bg-secondary' : ''
              }`}
              onClick={() => store.toggleSortAscending()}
            >
              <SortIcon className="size-5" />
            </button>
          </Tooltip>
        </div>
      </div>
    </div>
  );
}

function FilterSelectField({
  embedded,
  placeholder,
  widthClass,
  showClear,
  value,
  items,
  onValueChange,
  onClear,
}: {
  embedded: boolean;
  placeholder: string;
  widthClass: string;
  showClear: boolean;
  value?: string;
  items: SelectItem[];
  onValueChange: (value: string) => void;
  onClear: () => void;
}) {
  const selectClassName = showClear
    ? FILTER_SELECT_ACTIVE_CLASS_NAME
    : FILTER_SELECT_INACTIVE_CLASS_NAME;

  return (
    <div className="flex min-w-0 flex-none items-center gap-1">
      <div className={`min-w-0 ${widthClass}`}>
        <Select
          inModal={embedded}
          itemVariant="subtle"
          placeholder={placeholder}
          className={selectClassName}
          value={value}
          items={items}
          onValueChange={onValueChange}
          position="popper"
          side="bottom"
          align="start"
        />
      </div>
      {showClear ? (
        <button
          type="button"
          aria-label={`Clear ${placeholder} filter`}
          className={FILTER_CLEAR_BUTTON_CLASS_NAME}
          onClick={onClear}
        >
          <CrossIcon className="text-primary size-5" />
        </button>
      ) : null}
    </div>
  );
}
